<template>
  <div class="register-container">
    <div class="register-card">
      <h2 class="register-title">Crear cuenta</h2>
      <p class="register-subtitle">Regístrate para disfrutar de contenido exclusivo</p>

      <div v-if="error" class="alert error">{{ errorMessage }}</div>
      <div v-if="success" class="alert success">{{ successMessage }}</div>

      <form @submit.prevent="onSubmit">
        <div class="form-row">
          <div class="form-group">
            <input 
              type="text" 
              v-model="form.first_name" 
              placeholder="Nombre"
              :class="{ 'is-invalid': errors.first_name }"
              @input="clearError('first_name')"
            >
            <span class="error-message" v-if="errors.first_name">{{ errors.first_name }}</span>
          </div>
          <div class="form-group">
            <input 
              type="text" 
              v-model="form.last_name" 
              placeholder="Apellido"
              :class="{ 'is-invalid': errors.last_name }"
              @input="clearError('last_name')"
            >
            <span class="error-message" v-if="errors.last_name">{{ errors.last_name }}</span>
          </div>
        </div>

        <div class="form-group">
          <input 
            type="email" 
            v-model="form.email" 
            placeholder="Correo electrónico"
            :class="{ 'is-invalid': errors.email }"
            @input="clearError('email')"
          >
          <span class="error-message" v-if="errors.email">{{ errors.email }}</span>
        </div>

        <div class="form-group">
          <div class="password-wrapper">
            <input 
              :type="showPassword ? 'text' : 'password'" 
              v-model="form.password" 
              placeholder="Contraseña"
              :class="{ 'is-invalid': errors.password }"
              @input="clearError('password')"
            >
            <button type="button" class="toggle-password" @click="togglePasswordVisibility">
              <i class="material-icons">{{ showPassword ? 'visibility_off' : 'visibility' }}</i>
            </button>
          </div>
          <span class="error-message" v-if="errors.password">{{ errors.password }}</span>
        </div>

        <div class="form-group">
          <div class="password-wrapper">
            <input 
              :type="showConfirmPassword ? 'text' : 'password'" 
              v-model="form.password_confirmation" 
              placeholder="Confirmar contraseña"
              :class="{ 'is-invalid': errors.password_confirmation }"
              @input="clearError('password_confirmation')"
            >
            <button type="button" class="toggle-password" @click="toggleConfirmPasswordVisibility">
              <i class="material-icons">{{ showConfirmPassword ? 'visibility_off' : 'visibility' }}</i>
            </button>
          </div>
          <span class="error-message" v-if="errors.password_confirmation">{{ errors.password_confirmation }}</span>
        </div>

        <div class="form-row">
          <div class="form-group">
            <select v-model="form.gender" :class="{ 'is-invalid': errors.gender }">
              <option value="">Género (opcional)</option>
              <option value="Hombre">Hombre</option>
              <option value="Mujer">Mujer</option>
            </select>
            <span class="error-message" v-if="errors.gender">{{ errors.gender }}</span>
          </div>
          <div class="form-group">
            <input 
              type="tel" 
              v-model="form.phone" 
              placeholder="Teléfono (opcional)"
              :class="{ 'is-invalid': errors.phone }"
              @input="clearError('phone')"
            >
            <span class="error-message" v-if="errors.phone">{{ errors.phone }}</span>
          </div>
        </div>

        <button type="submit" :disabled="loading" class="btn-register">
          <span v-if="loading" class="spinner"></span>
          {{ loading ? 'Registrando...' : 'Registrarse' }}
        </button>

        <div class="login-link">
          ¿Ya tienes cuenta? <router-link to="/login" class="link">Inicia sesión aquí</router-link>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { useRouter } from 'vue-router'
import api from '../../services/api'

const router = useRouter()
const loading = ref(false)
const showPassword = ref(false)
const showConfirmPassword = ref(false)

const form = reactive({
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  password_confirmation: '',
  gender: '',
  phone: ''
})

const errors = reactive({
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  password_confirmation: '',
  gender: '',
  phone: ''
})

const clearError = (field) => {
  errors[field] = ''
}

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}

const toggleConfirmPasswordVisibility = () => {
  showConfirmPassword.value = !showConfirmPassword.value
}

const onSubmit = async () => {
  // Limpiar errores
  Object.keys(errors).forEach(key => errors[key] = '')
  
  // Validaciones básicas
  if (!form.first_name) errors.first_name = 'El nombre es requerido'
  if (!form.last_name) errors.last_name = 'El apellido es requerido'
  if (!form.email) errors.email = 'El correo electrónico es requerido'
  if (!form.password) errors.password = 'La contraseña es requerida'
  if (!form.password_confirmation) errors.password_confirmation = 'Confirma tu contraseña'
  
  if (form.password && form.password.length < 8) {
    errors.password = 'La contraseña debe tener al menos 8 caracteres'
  }
  
  if (form.password && form.password_confirmation && form.password !== form.password_confirmation) {
    errors.password_confirmation = 'Las contraseñas no coinciden'
  }
  
  if (Object.values(errors).some(e => e)) return
  
  loading.value = true

  try {
    const response = await api.post('/app/register', form)
    
    if (response.data.token) {
      localStorage.setItem('auth_token', response.data.token)
      localStorage.setItem('user', JSON.stringify(response.data.user))
      router.push('/home')
    }
  } catch (err) {
    if (err.response?.data?.errors) {
      const backendErrors = err.response.data.errors
      if (backendErrors.first_name) errors.first_name = backendErrors.first_name[0]
      if (backendErrors.last_name) errors.last_name = backendErrors.last_name[0]
      if (backendErrors.email) errors.email = backendErrors.email[0]
      if (backendErrors.password) errors.password = backendErrors.password[0]
      if (backendErrors.gender) errors.gender = backendErrors.gender[0]
      if (backendErrors.phone) errors.phone = backendErrors.phone[0]
    } else if (err.response?.data?.message) {
      errors.email = err.response.data.message
    } else {
      errors.email = 'Error al registrar el usuario'
    }
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.register-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
  padding: 20px;
}

.register-card {
  background: white;
  padding: 40px 30px;
  width: 100%;
  max-width: 500px;
}

.register-title {
  text-align: center;
  color: #00144a;
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 10px;
}

.register-subtitle {
  text-align: center;
  color: #666;
  font-size: 14px;
  margin-bottom: 30px;
}

.alert {
  padding: 12px;
  border-radius: 12px;
  margin-bottom: 20px;
}

.alert.success {
  background: #e8f5e9;
  color: #2e7d32;
  border-left: 4px solid #4caf50;
}

.alert.error {
  background: #ffebee;
  color: #f12711;
  border-left: 4px solid #f12711;
}

.form-row {
  display: flex;
  gap: 15px;
}

.form-group {
  margin-bottom: 20px;
  flex: 1;
}

input, select {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #ddd;
  border-radius: 12px;
  font-size: 16px;
  transition: all 0.3s;
  background: #f9f9f9;
}

input:focus, select:focus {
  outline: none;
  border-color: #00144a;
  background: white;
  box-shadow: 0 0 0 3px rgba(0, 20, 74, 0.1);
}

input.is-invalid, select.is-invalid {
  border-color: #f12711;
  background: rgba(241, 39, 17, 0.05);
}

.error-message {
  display: block;
  color: #f12711;
  font-size: 12px;
  margin-top: 5px;
}

.password-wrapper {
  position: relative;
}

.toggle-password {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  padding: 0;
}

.toggle-password i {
  font-size: 20px;
}

.toggle-password:hover {
  color: #00144a;
}

.btn-register {
  width: 100%;
  padding: 14px;
  background: #00144a;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
}

.btn-register:hover {
  background: #002a73;
  transform: translateY(-2px);
}

.btn-register:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.login-link {
  text-align: center;
  margin-top: 25px;
  padding-top: 20px;
  border-top: 1px solid #eee;
  font-size: 14px;
  color: #666;
}

.link {
  color: #00144a;
  text-decoration: none;
  font-weight: 600;
}

.link:hover {
  text-decoration: underline;
}

@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
    gap: 0;
  }
  
  .register-card {
    padding: 30px 20px;
  }
  
  input, select {
    padding: 12px 14px;
    font-size: 14px;
  }
  
  .register-title {
    font-size: 1.3rem;
  }
}
</style>